import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { categoriesApi } from '../../../api/categories.js';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { DataTable } from '../../../components/DataTable.jsx';
import { Pagination } from '../../../components/ui/Pagination.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Tabs } from '../../../components/ui/Tabs.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { t } from '../../../lib/i18n.js';

import { CategoryFormModal } from './CategoryFormModal.jsx';

const KIND_TABS = [
  { value: 'species', label: 'Specie' },
  { value: 'breed', label: 'Razze' },
  { value: 'productCategory', label: 'Categorie prodotto' }
];

const KIND_LABEL = {
  species: 'Specie',
  breed: 'Razza',
  productCategory: 'Categoria prodotto'
};

export function CategoriesPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();

  const canCreate = hasPermission('products:create');
  const canUpdate = hasPermission('products:update');
  const canDelete = hasPermission('products:delete');

  const [kind, setKind] = useState('species');

  const { items, isLoading, pagination, page, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['categories', 'list', kind],
    fetcher: (params) => categoriesApi.list({ ...params, kind }),
    initialFilters: {}
  });

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: (id) => categoriesApi.remove(id),
    onSuccess: () => {
      toast.success('Categoria eliminata.');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setConfirmTarget(null);
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const columns = [
    { key: 'name', header: 'Nome' },
    { key: 'slug', header: 'Slug', render: (r) => <code className="rounded bg-border/40 px-1.5 py-0.5 text-xs">{r.slug}</code> },
    {
      key: 'parent',
      header: 'Padre',
      render: (r) => (r.parentId && r.parentId.name) || <span className="text-ink-faint">-</span>
    },
    {
      key: 'order',
      header: 'Ordine',
      align: 'right',
      render: (r) => <span className="text-xs text-ink-muted">{r.order ?? '-'}</span>
    },
    {
      key: 'active',
      header: 'Stato',
      render: (r) => <Badge tone={r.active ? 'success' : 'neutral'}>{r.active ? 'Attiva' : 'Disattivata'}</Badge>
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-2">
          {canUpdate && (
            <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => { setEditing(r); setFormOpen(true); }}>
              Modifica
            </button>
          )}
          {canDelete && (
            <button type="button" className="text-xs text-danger hover:underline" onClick={() => setConfirmTarget(r)}>
              Elimina
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('nav.categories')}
        description="Tassonomia di specie, razze e categorie prodotto usata da prodotti e agenti AI."
        actions={canCreate ? <Button onClick={() => { setEditing(null); setFormOpen(true); }}>Nuova {KIND_LABEL[kind].toLowerCase()}</Button> : null}
      />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <Tabs value={kind} onChange={setKind} items={KIND_TABS} />
        <Input
          className="w-64"
          label="Cerca"
          placeholder="nome o slug..."
          value={filters.q || ''}
          onChange={(e) => setFilters({ q: e.target.value || undefined })}
        />
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={isLoading}
        empty={{ title: `Nessuna ${KIND_LABEL[kind].toLowerCase()}`, description: 'Aggiungi la prima voce per popolare la tassonomia.' }}
      />

      <Pagination pagination={pagination} onPageChange={setPage} />

      <CategoryFormModal
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        initial={editing}
        defaultKind={kind}
      />

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title="Eliminare la categoria?"
        description={confirmTarget ? `La categoria "${confirmTarget.name}" sarà rimossa.` : ''}
        destructive
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={() => deleteMutation.mutate(confirmTarget._id)}
      />
    </div>
  );
}
